import React from 'react';
import Divider from './components/Divider';
import VendorForm from './components/VendorForm';

function App() {
  return (
    <div className="min-h-screen bg-padua-ivory bg-subtle-texture py-12 px-4 sm:px-6 lg:px-8 selection:bg-padua-gold/30">
      
      {/* Main Invitation Card */}
      <div className="max-w-3xl mx-auto bg-padua-card shadow-[0_30px_60px_rgba(75,31,36,0.08)] relative">
        
        {/* Double Border Decorative Frames */}
        <div className="absolute inset-0 border-[1px] border-padua-border pointer-events-none"></div>
        <div className="absolute inset-2 border-[1px] border-padua-border/40 pointer-events-none"></div>
        <div className="absolute inset-4 border-[0.5px] border-padua-gold/20 pointer-events-none"></div>

        <div className="px-6 py-16 sm:px-16 sm:py-24 relative z-10">
          
          {/* Hero Section */}
          <header className="text-center mb-12">
            <h2 className="font-serif text-padua-gold tracking-[0.25em] uppercase text-xs sm:text-sm mb-6 font-semibold animate-pulse">
              Vendor Attendance Confirmation
            </h2>
            <h1 className="font-serif text-5xl sm:text-7xl text-padua-burgundy mb-6 leading-tight">
              The Padua<br/>
              <span className="italic">Midsummer Affair</span>
            </h1>

            {/* Event Date */}
            <div className="flex items-center justify-center space-x-4 mb-10">
              <div className="h-px bg-padua-gold/30 w-8 sm:w-12"></div>
              <span className="font-serif text-xl sm:text-2xl text-padua-gold tracking-[0.3em] uppercase">
                August 23, 2026
              </span>
              <div className="h-px bg-padua-gold/30 w-8 sm:w-12"></div>
            </div>

            <div className="max-w-2xl mx-auto space-y-6 mb-16">
              <p className="font-body text-padua-text text-lg leading-relaxed">
                We are delighted to invite a select circle of trusted wedding professionals to join us for The Padua Midsummer Affair, a complimentary wedding open house designed for engaged couples to experience Padua, meet curated vendors, tour the venue, enjoy food and drinks, and gather wedding inspiration.
              </p>
              <p className="font-body text-padua-text text-lg leading-relaxed italic opacity-90">
                Please confirm your attendance below so our team may finalize the vendor list, event layout, promotional details, and next steps.
              </p>
            </div>

            {/* Featured Invitation Graphic */}
            <div className="max-w-xl mx-auto relative group">
              <div className="absolute -inset-2 bg-padua-gold/5 transform rotate-1 rounded-sm transition-transform duration-700 group-hover:rotate-2"></div>
              <img 
                src="https://media-manager-c.questera.ai/greta-media/8c3dc8994f71a85ad2d71f226c89cc7edce114857865647a461869cb9a6c120ffe2f6c628544606d61dcf48135de6ac8/images/aW1hZ2UvcG5n/b7605246e29441b4bd1899f02aa5869f.png" 
                alt="The Padua Midsummer Affair Invitation" 
                className="relative z-10 w-full h-auto shadow-[0_20px_50px_rgba(75,31,36,0.15)] transform transition-transform duration-700 group-hover:scale-[1.02]"
              />
            </div>
          </header>

          <Divider />

          {/* Event Overview Section */}
          <section className="text-center mb-16">
            <h3 className="font-serif text-4xl text-padua-burgundy mb-8 italic">About the Affair</h3>
            <p className="font-body text-padua-text leading-relaxed mb-10 text-lg">
              The Padua Midsummer Affair will bring together engaged couples, curated wedding vendors, and the Padua Weddings team for an elevated evening of connection, inspiration, and hospitality.
            </p>
            
            <div className="bg-padua-ivory/40 border border-padua-border/30 p-10 max-w-lg mx-auto relative group">
              <div className="absolute -inset-1 border border-padua-gold/10 pointer-events-none group-hover:border-padua-gold/30 transition-colors duration-500"></div>
              <h4 className="font-serif text-padua-gold uppercase tracking-[0.2em] text-xs mb-6 border-b border-padua-border/30 pb-4">
                Event Highlights
              </h4>
              <ul className="font-body text-padua-text text-left space-y-4">
                {[
                  "Gourmet Food & Artisanal Drinks",
                  "Curated Circle of Wedding Vendors",
                  "Private Venue Tours",
                  "Exclusive Raffles & Giveaways",
                  "Immersive Wedding Inspiration",
                  "Special Winter Wedding Incentives"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="text-padua-gold mr-4 text-xs mt-1.5">◆</span>
                    <span className="text-[17px]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <Divider />

          {/* Vendor Confirmation Section - Form Embedded Directly */}
          <section className="text-center mb-16 px-4">
            <h3 className="font-serif text-4xl text-padua-burgundy mb-6 italic">Kindly Confirm Your Attendance</h3>
            <p className="font-body text-padua-text text-lg leading-relaxed mb-10 max-w-xl mx-auto">
              Your response will help our planning team prepare precise vendor placement, setup requirements, promotional mentions, and detailed event logistics.
            </p>
            
            <div className="mt-8">
              <VendorForm />
            </div>
            
            <p className="mt-8 font-body text-xs text-padua-gold uppercase tracking-widest opacity-70">
              Responses requested for the August 23, 2026 event
            </p>
          </section>

          <Divider />

          {/* Closing Section */}
          <section className="text-center pt-8 pb-4">
            <h3 className="font-serif text-3xl sm:text-4xl text-padua-burgundy mb-8 leading-snug">
              We Look Forward to Creating <br className="hidden sm:block" />
              <span className="italic">Something Beautiful Together</span>
            </h3>
            <div className="max-w-xl mx-auto">
              <p className="font-body text-padua-text text-lg leading-relaxed opacity-90">
                Thank you for your time, interest, and consideration. Our team will review each confirmation and follow up with additional event details, setup information, and arrival timing for August 23, 2026.
              </p>
            </div>
          </section>

        </div>
      </div>
      
      {/* Footer */}
      <footer className="text-center py-12">
        <div className="h-px bg-padua-border/30 w-16 mx-auto mb-4"></div>
        <p className="font-serif text-sm text-padua-burgundy tracking-[0.15em] uppercase opacity-60">
          The Padua Midsummer Affair • August 23, 2026
        </p>
      </footer>

    </div>
  );
}

export default App;